import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  ConsumableRateFormType,
  listConsumableItems,
  RateType,
  updateRate,
} from 'api/consumablesApi';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getBranches } from 'api/branchApi';
import { listClients } from 'api/clientsApi';
import {
  checkConsumableDuplication,
  filterConsumableRates,
} from '../Utils/Utils';
import { useRateForm } from '../useRateForm';
import ConsumableRateFormFields from './ConsumableRateFormFields';
import { colors } from '@material-ui/core';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';

interface Props {
  openSellRateForm: boolean;
  openBuyRateForm: boolean;
  handleSellRateFormClose: () => void;
  handleBuyRateFormClose: () => void;
}

function AddConsumableRates({
  openSellRateForm,
  openBuyRateForm,
  handleSellRateFormClose,
  handleBuyRateFormClose,
}: Props) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      consumableItemId: '',
      branch: null,
      clientId: null,
      country: '',
      state: '',
      sellRate: '',
      buyRate: '',
      isDefault: false,
      effectiveFrom: new Date().toISOString(),
    },
  });
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [rateId, setRateId] = useState('');
  const [formData, setFormData] = useState<ConsumableRateFormType | null>(null);
  const {
    addConsumableSellRateMutate,
    addConsumableBuyRateMutate,
    isAddingConsumableBuyRate,
    isAddingConsumableSellRate,
  } = useRateForm(
    openBuyRateForm ? handleBuyRateFormClose : handleSellRateFormClose
  );

  const { mutate: updateRateMutate } = useMutation(updateRate, {
    onSuccess: async () => {
      enqueueSnackbar('Successfully updated rate', { variant: 'success' });
      await queryClient.invalidateQueries(['consumables']);
      await queryClient.invalidateQueries(['consumables-items']);
      setFormData(null);
      openBuyRateForm ? handleBuyRateFormClose() : handleSellRateFormClose();
    },
    onError: () => {
      enqueueSnackbar('Failed to update rate', { variant: 'error' });
    },
  });

  const { data: branches } = useQuery(['branches'], getBranches);
  const consumableList = useQuery(['consumables-items'], listConsumableItems);
  const { data: clients } = useQuery(['list-clients'], listClients, {
    staleTime: 30 * 60 * 1000,
  });

  const { country, state, consumableItemId, clientId, branch } = watch([
    'country',
    'state',
    'consumableItemId',
    'clientId',
    'branch',
  ]);

  const filteredConsumableItems = React.useMemo(() => {
    if (!consumableList.data) return [];
    return consumableList.data.filter((item) => !item.isDeleted);
  }, [consumableList.data]);

  const buyTableRows = React.useMemo(() => {
    return filterConsumableRates(
      consumableList,
      {
        consumableItemId,
        clientId,
        branchId: branch?.id,
      },
      'buyRates'
    );
  }, [consumableList, country, state, consumableItemId, clientId, branch]);

  const sellTableRows = React.useMemo(() => {
    const filteredData = filterConsumableRates(
      consumableList,
      {
        consumableItemId,
        clientId,
        branchId: branch?.id,
      },
      'sellRates'
    );

    return filteredData;
  }, [consumableList, country, state, consumableItemId, clientId, branch]);

  const onSubmit = (data: ConsumableRateFormType) => {
    setFormData(data);
    const { checkRateDuplicate, foundRateId } = checkConsumableDuplication(
      data,
      consumableList.data
    );
    if (checkRateDuplicate) {
      setRateId(foundRateId);
      setOpenConfirmationDialog(true);
      return;
    } else {
      addNewRate(data);
    }
  };

  const addNewRate = (data: ConsumableRateFormType) => {
    if (openBuyRateForm) {
      addConsumableBuyRateMutate({ data });
    } else if (openSellRateForm) {
      addConsumableSellRateMutate({ data });
    }
  };

  const updateExistingRate = () => {
    const data = formData;
    if (openBuyRateForm) {
      updateRateMutate({
        id: rateId,
        data: data,
        rateType: RateType.BuyRate,
      });
    } else if (openSellRateForm) {
      updateRateMutate({
        id: rateId,
        data: data,
        rateType: RateType.SellRate,
      });
    }
  };

  return (
    <Dialog open={openBuyRateForm || openSellRateForm} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ paddingY: '1.5rem' }}>
        Add {openBuyRateForm ? 'Buy' : 'Sell'} Rate
      </DialogTitle>
      <Divider />
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ConsumableRateFormFields
            control={control}
            errors={errors}
            // isEditMode={!!editRateData}
            openBuyRateForm={openBuyRateForm}
            watch={watch}
            consumableItemNames={filteredConsumableItems}
            // editRateData={editRateData}
            filteredBranches={branches ? branches : []}
            openSellRateForm={openSellRateForm}
            clients={clients}
            sellTableRows={sellTableRows}
            buyTableRows={buyTableRows}
            setValue={setValue}
            // selectedTab={selectedTab}
          />
        </form>
      </DialogContent>
      <Dialog
        open={openConfirmationDialog}
        onClose={() => setOpenConfirmationDialog(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Override Existing Rate</DialogTitle>
        <Divider />
        <DialogContent>
          <Typography sx={{ marginTop: '1rem', marginBottom: '2rem' }}>
            You already have a rate for the selected consumable on the effective
            date. Do you want to override the existing rate or cancel to modify
            the effective date?
          </Typography>
          <Divider sx={{ marginY: '1rem' }} />
          <DialogActions sx={{ marginBottom: '0rem' }}>
            <Stack
              spacing={1.5}
              direction="row"
              sx={{ justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setOpenConfirmationDialog(false)}
                sx={{ color: colors.grey[600] }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  paddingX: '3rem',
                }}
                onClick={() => {
                  updateExistingRate();
                  setOpenConfirmationDialog(false);
                }}>
                Override Rate
              </Button>
            </Stack>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <Divider />
      <DialogActions sx={{ padding: '1.5rem' }}>
        <Button
          onClick={
            openBuyRateForm ? handleBuyRateFormClose : handleSellRateFormClose
          }
          sx={{ color: colors.grey[700], mr: 2 }}
          size="large">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isAddingConsumableBuyRate || isAddingConsumableSellRate}
          startIcon={
            isAddingConsumableBuyRate || isAddingConsumableSellRate ? (
              <CircularProgress size={20} color="inherit" />
            ) : null
          }
          size="large">
          Add {openBuyRateForm ? 'Buy' : 'Sell'} Rate
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddConsumableRates;
